import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { Button, cn } from "@clevis/shared-ui";

// Fit any image into the logo window before it's saved: trim empty edges,
// then Fit / Fill / Stretch, zoom, and drag to place it. The result is a
// 900 × 300 PNG (3 wide by 1 tall) — the shape every document and page uses.

export const LOGO_W = 900;
export const LOGO_H = 300;
const VIEW_W = 360;
const VIEW_H = 120;

type Mode = "fit" | "fill" | "stretch";

/** Cut away empty (clear or white) margins so the logo itself fills the window. */
function trimmed(img: HTMLImageElement): HTMLCanvasElement {
  const max = 2000;
  const s = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.max(1, Math.round(img.naturalWidth * s));
  const h = Math.max(1, Math.round(img.naturalHeight * s));
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d")!;
  ctx.drawImage(img, 0, 0, w, h);
  const data = ctx.getImageData(0, 0, w, h).data;
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const a = data[i + 3]!;
      const blank = a < 16 || (data[i]! > 242 && data[i + 1]! > 242 && data[i + 2]! > 242);
      if (blank) continue;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      if (y > y1) y1 = y;
    }
  }
  if (x1 < x0 || y1 < y0) return c; // nothing but background: keep it whole
  const pad = Math.round(Math.max(x1 - x0, y1 - y0) * 0.03);
  x0 = Math.max(0, x0 - pad);
  y0 = Math.max(0, y0 - pad);
  x1 = Math.min(w - 1, x1 + pad);
  y1 = Math.min(h - 1, y1 + pad);
  const out = document.createElement("canvas");
  out.width = x1 - x0 + 1;
  out.height = y1 - y0 + 1;
  out.getContext("2d")!.drawImage(c, x0, y0, out.width, out.height, 0, 0, out.width, out.height);
  return out;
}

function draw(ctx: CanvasRenderingContext2D, src: HTMLCanvasElement | HTMLImageElement, W: number, H: number, mode: Mode, zoom: number, off: { x: number; y: number }, white: boolean) {
  ctx.clearRect(0, 0, W, H);
  if (white) {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, W, H);
  }
  ctx.imageSmoothingQuality = "high";
  const k = W / LOGO_W; // offsets are kept in output pixels
  if (mode === "stretch") {
    const dw = W * zoom;
    const dh = H * zoom;
    ctx.drawImage(src, (W - dw) / 2 + off.x * k, (H - dh) / 2 + off.y * k, dw, dh);
    return;
  }
  const base = mode === "fit" ? Math.min(W / src.width, H / src.height) : Math.max(W / src.width, H / src.height);
  const s = base * zoom;
  const dw = src.width * s;
  const dh = src.height * s;
  ctx.drawImage(src, (W - dw) / 2 + off.x * k, (H - dh) / 2 + off.y * k, dw, dh);
}

export function LogoEditor({ file, busy, onCancel, onSave }: { file: File; busy: boolean; onCancel: () => void; onSave: (png: Blob) => void }) {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [trim, setTrim] = useState(true);
  const [mode, setMode] = useState<Mode>("fit");
  const [zoom, setZoom] = useState(1);
  const [off, setOff] = useState({ x: 0, y: 0 });
  const [white, setWhite] = useState(false);
  const [error, setError] = useState("");
  const view = useRef<HTMLCanvasElement>(null);
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const srcRef = useRef<HTMLCanvasElement | HTMLImageElement | null>(null);

  useEffect(() => {
    let dead = false;
    const url = URL.createObjectURL(file);
    const i = new Image();
    i.onload = () => !dead && (setError(""), setImg(i));
    i.onerror = () => !dead && setError("That image couldn't be opened. Try a PNG or JPG.");
    i.src = url;
    return () => {
      dead = true;
      URL.revokeObjectURL(url);
    };
  }, [file]);

  useEffect(() => {
    if (!img) return;
    srcRef.current = trim ? trimmed(img) : img;
  }, [img, trim]);

  useEffect(() => {
    const c = view.current;
    const src = srcRef.current;
    if (!c || !src || !img) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = VIEW_W * dpr;
    c.height = VIEW_H * dpr;
    draw(c.getContext("2d")!, src, c.width, c.height, mode, zoom, off, white);
  }, [img, trim, mode, zoom, off, white]);

  function start(e: ReactPointerEvent) {
    (e.target as Element).setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, ox: off.x, oy: off.y };
  }
  function move(e: ReactPointerEvent) {
    const d = drag.current;
    if (!d) return;
    const k = LOGO_W / VIEW_W;
    setOff({ x: d.ox + (e.clientX - d.x) * k, y: d.oy + (e.clientY - d.y) * k });
  }
  function save() {
    const src = srcRef.current;
    if (!src) return;
    const out = document.createElement("canvas");
    out.width = LOGO_W;
    out.height = LOGO_H;
    draw(out.getContext("2d")!, src, LOGO_W, LOGO_H, mode, zoom, off, white);
    out.toBlob((b) => (b ? onSave(b) : setError("Couldn't make the image. Try another file.")), "image/png");
  }
  const reset = () => {
    setZoom(1);
    setOff({ x: 0, y: 0 });
  };

  return (
    <div className="w-full space-y-2 rounded-lg border border-steel/20 bg-white p-3">
      <p className="text-xs text-steel">Drag to move it. The box is exactly what prints at the top of your documents.</p>
      <div
        className="mx-auto w-fit cursor-grab touch-none rounded border border-dashed border-brandorange/70 active:cursor-grabbing"
        style={{ backgroundImage: "repeating-conic-gradient(#eee 0% 25%, #fff 0% 50%)", backgroundSize: "16px 16px" }}
      >
        <canvas ref={view} style={{ width: VIEW_W, height: VIEW_H, display: "block" }} onPointerDown={start} onPointerMove={move} onPointerUp={() => (drag.current = null)} onPointerCancel={() => (drag.current = null)} />
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2 text-xs">
        <div className="flex rounded-lg border border-steel/30 p-0.5">
          {(["fit", "fill", "stretch"] as const).map((m) => (
            <button key={m} type="button" className={cn("rounded-md px-2.5 py-1 capitalize", mode === m ? "bg-ink text-white" : "text-steel")} onClick={() => { setMode(m); reset(); }}>
              {m}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-1 text-steel">
          Zoom
          <input type="range" min={0.5} max={4} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="w-28 accent-brandorange" />
        </label>
        <label className="flex items-center gap-1 text-steel">
          <input type="checkbox" checked={trim} onChange={(e) => { setTrim(e.target.checked); reset(); }} /> Trim empty edges
        </label>
        <label className="flex items-center gap-1 text-steel">
          <input type="checkbox" checked={white} onChange={(e) => setWhite(e.target.checked)} /> White background
        </label>
        <button type="button" className="text-steel underline hover:text-ink" onClick={reset}>
          Reset
        </button>
      </div>
      {error ? <p className="text-center text-xs text-danger">{error}</p> : null}
      <div className="flex justify-center gap-2">
        <Button className="px-3 py-1 text-xs" busy={busy} disabled={!img} onClick={save}>
          Save logo
        </Button>
        <Button variant="ghost" className="px-3 py-1 text-xs" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
